import React, { forwardRef, InputHTMLAttributes } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip'

interface CustomInputProps extends InputHTMLAttributes<HTMLInputElement> {
    label: string;
    error?: string;
    required?: boolean;
    icon?: React.ReactNode;
    onIconClick?: () => void;
    tooltip?: {
        id: string;
        content: string;
        place?: 'top' | 'bottom' | 'left' | 'right';
    };
    wrapperClassName?: string;
}

const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
    (
        {
            label,
            error,
            required,
            icon,
            onIconClick,
            className = '',
            tooltip,
            wrapperClassName = '',
            disabled,
            id,
            ...props
        },
        ref
    ) => {
        const isDisabled = disabled;
        const fallbackId = React.useId();
        const inputId = id || fallbackId;
        const errorId = `${inputId}-error`;

        return (
            <div className={`com-input-grp ${wrapperClassName}`}>
                <label
                    htmlFor={inputId}
                    className={`form-label ${isDisabled ? 'label-disabled' : ''}`}
                >
                    {label}

                    {tooltip && (
                        <>
                            <button
                                type="button"
                                data-tooltip-id={tooltip.id}
                                className="text-decoration-none cursor-pointer p-0 border-0 bg-transparent"
                                aria-label={`${label} information`}
                                style={{ display: 'inline', verticalAlign: 'middle' }}
                            >
                                <i
                                    className="icon-info ms-2"
                                    style={{ fontSize: '12px' }}
                                ></i>
                            </button>

                            <ReactTooltip
                                style={{
                                    zIndex: '1',
                                    wordBreak: 'break-word',
                                    maxWidth: '90%',
                                }}
                                id={tooltip.id}
                                place={tooltip.place || 'bottom'}
                                content={tooltip.content}
                            />
                        </>
                    )}

                    {required && <sup className="label-star">*</sup>}
                </label>

                <div className={icon ? 'position-relative' : 'income-field'}>
                    <input
                        id={inputId}
                        ref={ref}
                        disabled={isDisabled}
                        aria-invalid={!!error}
                        aria-describedby={error ? errorId : undefined}
                        className={`form-control 
                ${error ? 'is-invalid' : ''} 
                ${isDisabled ? 'input-disabled' : ''} 
                ${className}`}
                        {...props}
                    />

                    {icon && (
                        <div
                            role={onIconClick ? 'button' : undefined}
                            tabIndex={onIconClick && !isDisabled ? 0 : undefined}
                            className={`input-icon-right ${isDisabled ? 'icon-disabled' : ''
                                }`}
                            onClick={!isDisabled ? onIconClick : undefined}
                            onKeyDown={
                                onIconClick && !isDisabled
                                    ? (e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              onIconClick();
                                          }
                                      }
                                    : undefined
                            }
                            style={{
                                cursor: !isDisabled && onIconClick ? 'pointer' : 'default',
                            }}
                            aria-label={onIconClick ? "Input action" : undefined}
                        >
                            {icon}
                        </div>
                    )}
                </div>

                {error && <p id={errorId} className="valid-error" tabIndex={0} role="alert" aria-live="polite">{error}</p>}
            </div>
        );
    }
);


CustomInput.displayName = 'CustomInput';

export default CustomInput;